import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MovieCard from "../components/MovieCard";
import ContinueWatchingRail from "../components/ContinueWatchingRail";
import TopEarnersRail from "../components/TopEarnersRail";
import RisingRail from "../components/RisingRail";
import { Search, X } from "lucide-react";

const ALL_GENRE = "All";

export default function Browse() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [genre, setGenre] = useState(params.get("genre") || ALL_GENRE);
  const [cw, setCw] = useState([]);
  const [genreCatalog, setGenreCatalog] = useState([]);
  const urlQ = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(urlQ);

  // Load admin-managed genre catalog
  useEffect(() => {
    api.get("/genres").then((r) => setGenreCatalog(r.data || [])).catch(() => setGenreCatalog([]));
  }, []);

  const GENRES = [ALL_GENRE, ...genreCatalog.map((g) => g.name)];

  // Keep search box in sync if user hits Back/Forward.
  useEffect(() => { setSearchInput(urlQ); }, [urlQ]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    const search = new URLSearchParams();
    if (genre && genre !== "All") search.set("genre", genre);
    if (urlQ) search.set("q", urlQ);
    Promise.all([
      api.get(`/movies?${search.toString()}`).then((r) => r.data).catch(() => []),
      // Only fetch series when there's no search filter; series don't surface in search results today.
      urlQ ? Promise.resolve([]) : api.get("/series").then((r) => r.data).catch(() => []),
    ]).then(([m, s]) => {
      if (!active) return;
      setMovies(m);
      // If a genre filter is set, narrow series to that genre too
      const filteredSeries = (genre && genre !== "All") ? s.filter((x) => x.genre === genre) : s;
      setSeries(filteredSeries.filter((x) => x.episode_count > 0));
      setLoading(false);
    });
    return () => { active = false; };
  }, [genre, urlQ]);

  // Continue Watching for active subscribers (not for unauthenticated visitors)
  useEffect(() => {
    if (!user || user.subscription_status !== "active") { setCw([]); return; }
    api.get("/me/continue-watching").then(({ data }) => setCw(data)).catch(() => setCw([]));
  }, [user]);

  const applySearch = (e) => {
    e?.preventDefault();
    const trimmed = searchInput.trim();
    const np = new URLSearchParams(params);
    if (trimmed) np.set("q", trimmed); else np.delete("q");
    setParams(np);
  };
  const clearSearch = () => {
    setSearchInput("");
    const np = new URLSearchParams(params);
    np.delete("q");
    setParams(np);
  };

  const dismissCw = async (movieId) => {
    setCw((prev) => prev.filter((m) => m.id !== movieId));
    try { await api.delete(`/me/watch/${movieId}`); } catch { /* ignore */ }
  };

  // Group by genre for Netflix-style rows when no specific filter
  const grouped = (() => {
    if (genre !== "All" || urlQ) return null;
    const map = new Map();
    movies.forEach((m) => {
      if (!map.has(m.genre)) map.set(m.genre, []);
      map.get(m.genre).push(m);
    });
    return map;
  })();

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="browse-page">
      <div className="flex flex-col gap-6 mb-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div>
            <p className="text-eyebrow">Library</p>
            <h1 className="font-display text-4xl sm:text-5xl font-black mt-2 tracking-tighter">
              {urlQ ? <>Results for <span className="text-[#E65F2B]">&ldquo;{urlQ}&rdquo;</span></> : <>Discover <span className="text-[#E65F2B]">Christian Cinema</span></>}
            </h1>
          </div>
          <form onSubmit={applySearch} className="relative w-full sm:max-w-sm" data-testid="browse-search-form">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search titles, channels…"
              className="input-dark pl-10 pr-10"
              data-testid="browse-search-input"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Clear search"
                data-testid="browse-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </form>
        </div>
      </div>

      {/* Continue Watching only when not filtering / searching */}
      {!urlQ && genre === "All" && (
        <ContinueWatchingRail items={cw} onDismiss={dismissCw} />
      )}

      {/* Top-earning films this month — live social proof for viewers + creator recruiting.
          Hidden during search/genre filtering to keep the rail contextually relevant. */}
      {!urlQ && genre === "All" && <TopEarnersRail />}

      {/* Rising this week — momentum leaderboard. Answers the different, more
          human question: "what's catching fire right now?" Protects mid-tier
          creators from being drowned out by top-of-catalog perennial #1s. */}
      {!urlQ && genre === "All" && <RisingRail />}

      {/* Series rail — show when there are any series with episodes */}
      {!urlQ && series.length > 0 && (
        <section className="mb-12" data-testid="series-rail">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-display text-2xl sm:text-3xl font-bold">
              <span className="text-[#E65F2B]">Series</span> & shows
            </h2>
            <span className="text-xs text-white/40">{series.length} title{series.length === 1 ? "" : "s"}</span>
          </div>
          <div className="flex gap-4 overflow-x-auto row-scroll pb-4">
            {series.map((s, i) => (
              <Link
                key={s.id}
                to={`/series/${s.id}`}
                data-testid={`series-tile-${s.id}`}
                className="w-64 sm:w-72 shrink-0 group relative animate-fade-up"
                style={{ animationDelay: `${i * 60}ms`, opacity: 0 }}
              >
                <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gradient-to-br from-[#1F1F1F] via-[#141414] to-black border border-white/5 group-hover:border-[#E65F2B]/40 transition-colors relative">
                  <div className="absolute inset-0 p-5 flex flex-col justify-between">
                    <div>
                      <div className="inline-flex items-center gap-1.5 bg-[#E65F2B]/20 border border-[#E65F2B]/40 px-2 py-1 rounded">
                        <span className="h-1.5 w-1.5 rounded-full bg-[#E65F2B]" />
                        <span className="text-[10px] uppercase tracking-wider text-[#E65F2B] font-bold">Series</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-white/40">{s.genre}</p>
                      <h3 className="font-display text-2xl font-black leading-tight mt-1 line-clamp-3">{s.title}</h3>
                      <p className="text-xs text-white/40 mt-3">
                        {s.episode_count} episode{s.episode_count === 1 ? "" : "s"}
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <div className="flex flex-wrap gap-2 mb-10 overflow-x-auto row-scroll" data-testid="genre-filters">
        {GENRES.map((g) => (
          <button
            key={g}
            onClick={() => {
              setGenre(g);
              const np = new URLSearchParams(params);
              if (g === "All") np.delete("genre"); else np.set("genre", g);
              setParams(np);
            }}
            data-testid={`genre-${g.toLowerCase().replace(/\s/g, "-")}`}
            className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-all border ${
              genre === g ? "bg-[#E65F2B] border-[#E65F2B] text-white" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
            }`}
          >
            {g}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="aspect-[2/3] bg-[#141414] rounded-lg animate-pulse" />
          ))}
        </div>
      ) : movies.length === 0 ? (
        <div className="text-center py-24" data-testid="browse-empty">
          <Search className="h-12 w-12 mx-auto text-white/20 mb-4" />
          <h3 className="font-display text-2xl font-bold">No titles yet</h3>
          <p className="text-white/50 mt-2 max-w-md mx-auto">
            {urlQ ? "No movies matched your search. Try another keyword or browse all genres." : "The library is just getting started. Check back soon — or become a creator and add the first title."}
          </p>
        </div>
      ) : grouped ? (
        <div className="space-y-12">
          {Array.from(grouped.entries()).map(([gn, list]) => {
            const gMeta = genreCatalog.find((g) => g.name === gn);
            return (
            <div key={gn} data-testid={`row-${gn.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-baseline justify-between mb-4 gap-3">
                {gMeta ? (
                  <Link to={`/genres/${gMeta.slug}`} className="font-display text-2xl sm:text-3xl font-bold hover:text-[#E65F2B] transition group inline-flex items-center gap-2" data-testid={`row-shelf-link-${gMeta.slug}`}>
                    {gn} <span className="text-base text-white/30 group-hover:text-[#E65F2B] transition">→</span>
                  </Link>
                ) : (
                  <h2 className="font-display text-2xl sm:text-3xl font-bold">{gn}</h2>
                )}
                <span className="text-xs text-white/40">{list.length} titles</span>
              </div>
              <div className="flex gap-4 overflow-x-auto row-scroll pb-4">
                {list.map((m, i) => <MovieCard key={m.id} movie={m} index={i} />)}
              </div>
            </div>
          );})}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-testid="browse-grid">
          {movies.map((m, i) => <MovieCard key={m.id} movie={m} index={i} />)}
        </div>
      )}
    </div>
  );
}
